// external modules
import { Modals } from "@becon-team/becon-fe-lib/my-modal";
import queryString from "query-string";
import { Suspense, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import store from "store";
import "./libs/i18n";
// internal modules
import { useScrollToTopOnLoad } from "@becon-team/becon-fe-lib/hooks";
import loadable from "@loadable/component";
import { useGetAccountInfo } from "api/@auth/query_auth";
import { useHandleKeyboardShow } from "hooks/@common/useHandleKeyboradShow";
import useFcmToken from "hooks/FcmToken/useFcmToken";
import useOnErrorAction from "hooks/Native/useOnErrorAction";
import useOnResultAction from "hooks/Native/useOnResultAction";
import Intro from "pages/Intro/Intro";
import { marketingAgreementState } from "store/recoil/@auth/marketingAgreementState";
import { appBackgroundColorState } from "store/recoil/@common/appBackgroundColorState";
import { useAppDispatch, useAppSelector } from "store/redux/modules";
import { getAccountAction } from "store/redux/modules/account";
import { getUsers } from "store/redux/modules/user";
import { path } from "utils/(path)";
import { quickStyle } from "utils/@common/quickStyle";
import { getDeviceInfo } from "utils/nativeApi";
// state
import { currentCenterModalState } from "store/recoil/@common/centerModalState";
import { getIsActiveLoginBottomSheet } from "store/redux/modules/account";
// elements
import { MainLayout } from "components/(layout)/MainLayout";
import { NoNavBarLayout } from "components/(layout)/NoNavBarLayout";
import AppVersionNotice from "components/@common/AppVersionNotice/AppVersionNotice";
import CenterModal from "components/@common/CenterModal/CenterModalOverlay";
import Header from "components/@common/Header/Header";
import { Loader } from "components/@common/Loader/Loader";
import CrownRouter from "pages/Crown";
// import MicroSkinRouter from "pages/MicroSkin";
import { Toasters } from "@becon-team/becon-fe-lib/my-toast";
import { localStorageKeyLastSignInEmail } from "utils/(localStorage)";

// types
const MainPage = loadable(() => import("pages/MainPage"), { fallback: <Loader /> });

const BlogPage = loadable(() => import("pages/Blog"));
const CommunityMainPage = loadable(() => import("pages/Community/components/Main"), { fallback: <Loader /> });
const CommunityContainer = loadable(() => import("pages/Community"), { fallback: <Loader /> });
const SkinContainer = loadable(() => import("pages/Skin/index"), { fallback: <Loader /> });
const ShampooPage = loadable(() => import("pages/Shampoo/page"), { fallback: <Loader /> });
const WeatherPage = loadable(() => import("pages/Weather/page"), { fallback: <Loader /> });
const ScalpContainer = loadable(() => import("pages/Scalp/index"), { fallback: <Loader /> });
const AuthRouter = loadable(() => import("pages/Auth"), { fallback: <Loader /> });
const TermsRouter = loadable(() => import("pages/Terms"), { fallback: <Loader /> });
const ForeheadPage = loadable(() => import("pages/Forehead"), { fallback: <Loader /> });
const QuizPage = loadable(() => import("pages/QuizPage"), { fallback: <Loader /> });
const ShampooCompatibilityRouter = loadable(() => import("pages/ShampooCompatibility"), { fallback: <Loader /> });
const MyPage = loadable(() => import("pages/DrawerPage/Main"), { fallback: <Loader /> });
const DrawerPage = loadable(() => import("pages/DrawerPage"), { fallback: <Loader /> });
const PartnerShipPage = loadable(() => import("pages/PartnerShipPage"), { fallback: <Loader /> });
const BeconTubePage = loadable(() => import("pages/BeconTube"), { fallback: <Loader /> });
const TutorialPage = loadable(() => import("pages/TutorialPage"), { fallback: <Loader /> });
const HistoryRouter = loadable(() => import("pages/History"), { fallback: <Loader /> });
const BTheBB2BHistory = loadable(() => import("pages/History/B2BHistory/btheb"), { fallback: <Loader /> });
const BigDataPage = loadable(() => import("pages/BigData/page"), { fallback: <Loader /> });

export default function App() {
  // =================== state =================== //
  const { isAuthenticated, users, navTab, fcmToken } = useAppSelector(({ account, user, status }) => {
    return {
      isAuthenticated: account.account?.account_id ? true : false,
      users: user.users,
      language: status.language,
      3: status.firstOpen,
      navTab: status.navTab,
      fcmToken: status.fcmToken,
    };
  });

  const isActiveLoginBottomSheet = useAppSelector(getIsActiveLoginBottomSheet);
  const [isOpenCenterModal, setIsOpenCenterModal] = useRecoilState(currentCenterModalState);
  const setAppBackgroundColor = useSetRecoilState(appBackgroundColorState);
  const setMarketingAgreement = useSetRecoilState(marketingAgreementState);

  // =================== hooks ===================
  useHandleKeyboardShow();
  const dispatch = useDispatch();
  useFcmToken(fcmToken, users);
  const { search } = useLocation();

  type TFrom = "native" | "website";
  const { from, refresh_token }: { from?: TFrom; refresh_token?: string } = queryString.parse(search);
  const location = useLocation();
  /* iOS mobile viewport bug fix */

  /** set app/safe-area background color  (safe-area와 함께 app bg color를 컨트롤 해야 할때 조작해주세요.)*/
  useEffect(() => {
    switch (location.pathname) {
      case "/intro":
        setAppBackgroundColor("primary-dark-2");
        break;
      case "/forehead":
        setAppBackgroundColor("etc-pink-light");
        break;
      case "/shampoo":
        setAppBackgroundColor("etc-purple-dark");
        break;
      default:
        setAppBackgroundColor("gray-white");
    }
  }, [location.pathname]);

  // useEffect(() => {
  //   if (isValidVersion) {
  //     setIsOpenCenterModal(false);
  //   } else {
  //     setIsOpenCenterModal(true);
  //   }
  // }, [isValidVersion, setIsOpenCenterModal]);

  useEffect(() => {
    if (!users) return;
    setMarketingAgreement(users[0].marketing_agreement); // NOTE: marketing_agreement 초기값 set
  }, [users]);

  // =================== 네이티브(AOS, IOS) -> 웹뷰 통신 정의  =================== //
  useOnResultAction();
  useOnErrorAction();

  useEffect(() => {
    getDeviceInfo();
  }, []);

  return (
    <Suspense fallback={<Loader />}>
      <ScrollToTopOnLoad />
      <Toasters />
      {isOpenCenterModal && location.pathname === "/" && (
        <CenterModal>
          <AppVersionNotice />
        </CenterModal>
      )}
      <Header />
      <Modals />
      <RequireAuth>
        <>
          <Routes>
            <Route element={<Intro />} path={"/intro"} />
            <Route element={<WeatherPage />} path={"/weather" + "/*"} />
            <Route element={<MainLayout />}>
              <Route element={<MainPage />} path={"/"} />
              <Route element={<MyPage />} path={"/drawer"} />
              <Route element={<CommunityMainPage />} path={path.community()} />
            </Route>
            <Route element={<NoNavBarLayout />}>
              <Route element={<BlogPage />} path={"/blog" + "/*"} />
              <Route element={<CommunityContainer />} path={path.community() + "/*"} />
              <Route element={<ForeheadPage />} path={path.forehead() + "/*"} />
              <Route element={<ScalpContainer />} path={path.scalp() + "/*"} />
              <Route element={<SkinContainer />} path={path.skin() + "/*"} />
              <Route element={<QuizPage />} path={"/quiz/:id"} />
              <Route element={<ShampooCompatibilityRouter />} path={"/shampoo" + "/*"} />
              <Route element={<DrawerPage />} path={"/drawer" + "/*"} />
              <Route element={<PartnerShipPage />} path={"/partnership"} />
              <Route element={<BeconTubePage />} path={"/becon-tube"} />
              <Route element={<AuthRouter />} path={"/auth" + "/*"} />
              <Route element={<TermsRouter />} path={"/terms" + "/*"} />
              <Route element={<TutorialPage />} path={"/tutorial"} />
              <Route element={<ShampooPage />} path={path.shampoo()} />
              <Route element={<BigDataPage />} path={path.bigData()} />
              <Route element={<CrownRouter />} path={path.crown() + "/*"} />
              <Route element={<HistoryRouter />} path={path.history() + "/*"} />
              {/* *주의* 비컨프로와 연동 */}
              <Route element={<BTheBB2BHistory />} path={"/analysis-history-b2b"} />
            </Route>
          </Routes>
        </>
      </RequireAuth>
      <SafeAreaTop />
      <SafeAreaBottom />
    </Suspense>
  );
}

function SafeAreaTop() {
  const appBackgroundColor = useRecoilValue(appBackgroundColorState);
  return <div className={`safeAreaTop ${quickStyle.backgroundColor(appBackgroundColor)}`} />;
}

function SafeAreaBottom() {
  const location = useLocation();
  const isMainPage =
    location.pathname === "/" ||
    location.pathname === "/community" ||
    location.pathname === "/analysis" ||
    location.pathname === "/intro" ||
    location.pathname === "/drawer";

  const appBackgroundColor = useRecoilValue(appBackgroundColorState);
  return (
    <div
      className={`safeAreaBottom ${quickStyle.backgroundColor(appBackgroundColor)}`}
      data-is-nav-nonexistent={!isMainPage}
    />
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const dispatch = useAppDispatch();
  const { data: myAccount, isFetching: isFetchingMyAccount } = useGetAccountInfo();
  const location = useLocation();

  useEffect(() => {
    if (!myAccount) return;

    const userEmail = myAccount.data?.account?.email;
    dispatch(getUsers());
    dispatch(getAccountAction());

    if (userEmail) {
      store.set(localStorageKeyLastSignInEmail, userEmail);
    }
  }, [myAccount, dispatch]);

  if (isFetchingMyAccount) return null;

  /** 인증되지 않은 상태에서 페이지가 /intro,/auth,/terms 가 아닌 경우 =>  /intro   */
  if (!myAccount && !["/intro", path.auth(), "/terms"].some((path) => location.pathname.includes(path))) {
    return <Navigate to="/intro" />;
  }

  return children;
}

const ScrollToTopOnLoad = () => {
  const { pathname } = useLocation();
  useScrollToTopOnLoad(pathname);
  return null;
};
